/**
 * useInventoryStream — streaming intelligence accumulator.
 *
 * VariantCShell holds only the LATEST InventoryResolveEvent in state
 * (one tick per SSE message). This hook watches the event prop for
 * reference changes and builds a local history in a ref so the
 * streaming timeline components can display the full event sequence.
 *
 * Design contract:
 *   - NO fake timers. Every piece of derived state maps 1:1 to a
 *     real SSE event that arrived over the wire.
 *   - When loading + event are both cleared (stream done / unmount),
 *     the history is reset so the next stream starts fresh.
 *   - Returns a stable `InventoryStreamState` object; only the fields
 *     that changed on the last tick differ, so downstream memo checks
 *     on individual fields are effective.
 *
 * Usage:
 *   const stream = useInventoryStream(resolveEvent, loading);
 *   // stream.phase, stream.progress, stream.eventLog, …
 */

import { useEffect, useMemo, useReducer, useRef } from "react";
import type { InventoryResolveEvent } from "../../../api/client";

// ─── Public types ──────────────────────────────────────────────────

/**
 * Coarse phase derived from the current SSE event type.
 * Used by StreamingStatusBar to pick the right label / colour.
 */
export type StreamPhase =
  | "idle" // no stream active
  | "checking" // checking_cache, cache_hit, cache_miss
  | "classifying" // classifying, labelled, params_extracting, params_extracted
  | "done" // type === "done"
  | "error"; // type === "error"

/**
 * One entry in the streaming event log shown by InventoryTimeline.
 * Accumulated from successive event ticks.
 */
export interface TimelineEntry {
  /** Stable React key — type + monotonic stamp to avoid collisions. */
  id: string;
  type: InventoryResolveEvent["type"];
  /** Human-readable summary of this event. */
  label: string;
  /** Filename involved, if any (classifying / labelled / params). */
  docName?: string;
  /** Batch position label, e.g. "3 / 12". */
  position?: string;
  /** ms since `performance.now()` at stream start. */
  elapsed: number;
  /** Label appended next to the entry: resolved_by, label value, etc. */
  meta?: string;
}

export interface InventoryStreamState {
  phase: StreamPhase;
  /** 0..1 — driven by classifying.index/total when available.
   *  Stays at 0 during indeterminate phases (checking). */
  progress: number;
  /** True when no position data is available → use shimmer track. */
  isIndeterminate: boolean;
  /** Filename currently being classified (from `classifying` event). */
  currentDocName: string | null;
  currentDocIndex: number | null;
  totalDocs: number | null;
  /** Full accumulated event log for InventoryTimeline. */
  eventLog: TimelineEntry[];
  /** True while `loading` is true and stream hasn't ended. */
  isLive: boolean;
  /** ms since first event in this stream, updated on each tick. */
  elapsedMs: number;
}

// ─── Event label formatter ─────────────────────────────────────────

function eventLabel(ev: InventoryResolveEvent): string {
  switch (ev.type) {
    case "checking_cache":
      return "Checking cache…";
    case "cache_hit":
      return `Cache hit${ev.state ? ` · ${ev.state}` : ""}`;
    case "cache_miss":
      return "Cache miss — classifying";
    case "classifying": {
      const hasPos = typeof ev.index === "number" && typeof ev.total === "number";
      if (ev.doc_name && hasPos) return `Classifying ${ev.doc_name}`;
      if (hasPos) return `Classifying ${ev.index}/${ev.total}`;
      return ev.doc_name ? `Classifying ${ev.doc_name}` : "Classifying…";
    }
    case "labelled":
      return ev.doc_name ? `Understood ${ev.doc_name}` : "Document understood";
    case "params_extracting":
      return ev.doc_name ? `Extracting params: ${ev.doc_name}` : "Extracting parameters…";
    case "params_extracted":
      return ev.doc_name ? `Params ready: ${ev.doc_name}` : "Parameters extracted";
    case "done":
      return "Library intelligence ready";
    case "error":
      return `Error: ${ev.error ?? "unknown"}`;
  }
}

function eventMeta(ev: InventoryResolveEvent): string | undefined {
  if (ev.type === "labelled" && ev.resolved_by) {
    return ev.resolved_by === "cache" ? "cached" : `via ${ev.resolved_by}`;
  }
  if (ev.type === "cache_hit" && typeof ev.doc_count === "number") {
    return `${ev.doc_count} docs`;
  }
  return undefined;
}

function eventPosition(ev: InventoryResolveEvent): string | undefined {
  if (
    (ev.type === "classifying" || ev.type === "labelled") &&
    typeof (ev as { index?: number }).index === "number" &&
    typeof (ev as { total?: number }).total === "number"
  ) {
    const e = ev as { index: number; total: number };
    return `${e.index} / ${e.total}`;
  }
  return undefined;
}

// ─── Phase derivation ──────────────────────────────────────────────

function derivePhase(ev: InventoryResolveEvent | null): StreamPhase {
  if (!ev) return "idle";
  switch (ev.type) {
    case "checking_cache":
    case "cache_hit":
    case "cache_miss":
      return "checking";
    case "classifying":
    case "labelled":
    case "params_extracting":
    case "params_extracted":
      return "classifying";
    case "done":
      return "done";
    case "error":
      return "error";
  }
}

function deriveProgress(ev: InventoryResolveEvent | null): number {
  if (!ev) return 0;
  if (ev.type === "done") return 1;
  if (
    (ev.type === "classifying" || ev.type === "labelled") &&
    typeof (ev as { index?: number }).index === "number" &&
    typeof (ev as { total?: number }).total === "number"
  ) {
    const e = ev as { index: number; total: number };
    return e.total > 0 ? e.index / e.total : 0;
  }
  if (ev.type === "params_extracted") return 1;
  return 0;
}

// ─── Hook ──────────────────────────────────────────────────────────

const IDLE: InventoryStreamState = {
  phase: "idle",
  progress: 0,
  isIndeterminate: true,
  currentDocName: null,
  currentDocIndex: null,
  totalDocs: null,
  eventLog: [],
  isLive: false,
  elapsedMs: 0,
};

export function useInventoryStream(event: InventoryResolveEvent | null, loading: boolean): InventoryStreamState {
  const historyRef = useRef<TimelineEntry[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const prevEventRef = useRef<InventoryResolveEvent | null>(null);

  // Force a re-render when history changes without storing history
  // in React state (avoids double-render on every SSE tick).
  const [renderCount, rerender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    // Stream cleared → reset all accumulated state.
    if (!loading && !event) {
      historyRef.current = [];
      startedAtRef.current = null;
      prevEventRef.current = null;
      rerender();
      return;
    }

    // New event arrived (reference change — each SSE tick is a new
    // object from the JSON.parse in streamLibraryInventory).
    if (event && event !== prevEventRef.current) {
      prevEventRef.current = event;

      if (startedAtRef.current === null) {
        startedAtRef.current = performance.now();
      }
      const elapsed = performance.now() - (startedAtRef.current ?? 0);

      const entry: TimelineEntry = {
        id: `${event.type}-${elapsed.toFixed(0)}`,
        type: event.type,
        label: eventLabel(event),
        docName: "doc_name" in event ? (event as { doc_name?: string }).doc_name : undefined,
        position: eventPosition(event),
        elapsed,
        meta: eventMeta(event),
      };

      historyRef.current = [...historyRef.current, entry];
      rerender();
    }
  }, [event, loading]);

  // Derive current stream state from the latest event + history.
  return useMemo(() => {
    if (!loading && !event && historyRef.current.length === 0) {
      return IDLE;
    }

    const phase = derivePhase(event);
    const progress = deriveProgress(event);
    const isIndeterminate = phase === "idle" || phase === "checking" || phase === "error";

    let currentDocName: string | null = null;
    let currentDocIndex: number | null = null;
    let totalDocs: number | null = null;

    if (event?.type === "classifying") {
      const e = event as {
        doc_name?: string;
        index?: number;
        total?: number;
      };
      currentDocName = e.doc_name ?? null;
      currentDocIndex = typeof e.index === "number" ? e.index : null;
      totalDocs = typeof e.total === "number" ? e.total : null;
    }

    // renderCount ensures the memo re-runs each time the history ref
    // changes (since refs don't trigger re-renders by default).
    void renderCount;
    return {
      phase,
      progress,
      isIndeterminate,
      currentDocName,
      currentDocIndex,
      totalDocs,
      eventLog: historyRef.current,
      isLive: loading && phase !== "done" && phase !== "error",
      elapsedMs: startedAtRef.current !== null ? performance.now() - startedAtRef.current : 0,
    };
  }, [event, loading, renderCount]);
}
